"use client";
// src/lib/convex/client.tsx
// ConvexReactClient and Provider for client-side Convex access
// Uses the official Convex + Better Auth integration
//
// ── Reads ──────────────────────────────────────────────────────────────────
// Every query read goes through the one reader (src/lib/convex/hooks/useAuthedQuery.ts) and the query cache this
// provider mounts (ConvexQueryCacheProvider), in the browser: the server reads no page data and hands down no
// preloaded tokens (CLAUDE.md → Reads).

import { authClient } from "@/lib/auth/client";
import { buildDebugEntryKey, isConvexDebugRecordingEnabled, recordConvexDebugEntry, subscribeConvexDebugRecordingEnabled } from "@/lib/convex/debug-store";
import { DiagnosticsBootstrap } from "@/lib/diagnostics/DiagnosticsBootstrap";
import { errorToDiagnosticsFields } from "@/lib/diagnostics/normalize";
import { recordDiagnosticsFromClient } from "@/lib/diagnostics/record-client";
import clone from "@/utils/clone.mts";
import { type AuthClient, ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { ConvexQueryCacheProvider } from "convex-helpers/react/cache/provider";
import { ConvexReactClient, type Watch, type WatchQueryOptions } from "convex/react";
import { type ArgsAndOptions, type FunctionReference, type FunctionReturnType, getFunctionName } from "convex/server";
import { memo, ReactNode, useMemo } from "react";

/**
 * Process-wide {@link ConvexReactClient} created by {@link getConvexClient}.
 *
 * Starts `null` and is assigned on the first successful construction. Nothing
 * in this module clears it, so a remount of {@link ConvexClientProvider} reuses
 * the same socket rather than opening a second one. In development the stored
 * instance is the debug-patched client; in production it is the raw constructor
 * result.
 *
 * @type {(ConvexReactClient|null)}
 */
let convexClient: ConvexReactClient | null = null;

/**
 * The two {@link Watch} methods the debug layer actually calls.
 *
 * Convex's full `Watch` also carries `journal` / local-state helpers this file
 * never reads. Both `watchQuery` and the optional `watchPaginatedQuery` are
 * treated as this shape so {@link recordWatchValue} can call `localQueryResult`
 * without threading Convex's generic `Watch<T>` through the map. `onUpdate`
 * returns Convex's unsubscribe. `localQueryResult` is `undefined` while the
 * first result is in flight, an `Error` instance on failure, or the query
 * payload when ready — and may throw instead of returning the error.
 *
 * @typedef {{onUpdate: (callback: () => void) => () => void, localQueryResult: () => unknown}} ConvexWatchLike
 */
type ConvexWatchLike = {
  onUpdate: (callback: () => void) => () => void;
  localQueryResult: () => unknown;
};

/**
 * Optional `watchPaginatedQuery` on the React client, if the installed
 * `convex/react` build exposes it.
 *
 * Args and options stay `unknown` because this file does not import Convex's
 * pagination option types; {@link patchConvexClientForDebug} only wraps the
 * method when it is present on the instance.
 *
 * @callback ConvexPaginatedWatchQueryMethod
 * @param {FunctionReference<"query">} query - Paginated query function reference.
 * @param {unknown} args - Arguments Convex's paginated hook already built including `paginationOpts`.
 * @param {unknown} options - Watch options forwarded untouched.
 * @returns {ConvexWatchLike} The live paginated watch.
 */
/**
 * {@link ConvexReactClient} plus the two own-properties the debug patch writes.
 *
 * `__dwConvexDebugPatched` is set to `true` on the singleton the first time
 * {@link patchConvexClientForDebug} runs, so Fast Refresh / a second `getConvexClient` call does not stack wrappers. `watchPaginatedQuery` is absent on builds that do not ship it.
 *
 */
/**
 * One live `watchQuery` / `watchPaginatedQuery` currently tracked for the
 * Convex debug panel.
 *
 * `key` is {@link debugWatchKey} of `(kind, name, args)` — the same string
 * {@link buildDebugEntryKey} uses in the debug store — so a later watch of the
 * same triple replaces this record in {@link activeDebugWatches}. `name` is
 * `getFunctionName(query)` and becomes the diagnostics `action`. `args` is
 * the first watch argument (or `{}` when omitted) and is cloned before either
 * sink sees it. `watch` is the original Convex watch.
 */
type ConvexDebugWatchRecord = {
  key: string;
  kind: "query" | "paginatedQuery";
  name: string;
  args: unknown;
  watch: ConvexWatchLike;
};

/**
 * Live debug watches keyed by {@link debugWatchKey}.
 *
 * {@link registerDebugWatch} only `set`s — there is no unregister when a React
 * hook unsubscribes — so this map grows with every distinct `(kind, name, args)`
 * triple seen since the singleton was created. {@link flushDebugWatches} still
 * snapshots every occupant when recording is turned on.
 *
 */
const activeDebugWatches = new Map<string, ConvexDebugWatchRecord>();

/**
 * Whether a full-map flush is already scheduled on rAF / `setTimeout`.
 *
 * {@link scheduleDebugFlush} sets this; {@link flushDebugWatches} clears it as
 * its first statement, even when recording has been turned off in the meantime.
 *
 */
let debugFlushQueued = false;

/**
 * Unsubscribe for the one {@link subscribeConvexDebugRecordingEnabled} listener
 * this module installs.
 *
 * Set by {@link ensureDebugEnabledSubscription} and never called — the
 * listener lives for the life of the page so toggling the debug panel's
 * recording switch can flush the map.
 *
 */
let debugEnabledSubscription: (() => void) | null = null;

/**
 * Occupancy key for {@link activeDebugWatches}.
 *
 * Delegates to {@link buildDebugEntryKey} so a watch and its debug-store row
 * share the same `kind:name:stableJson(normalizedArgs)` string.
 * {@link registerDebugWatch} is the only caller; a colliding key overwrites
 * the previous record.
 *
 * @param {("query"|"paginatedQuery")} kind - Watch method that produced the subscription.
 * @param {string} name - Convex function name from `getFunctionName`.
 * @param {unknown} args - Query args; pagination `id` is stripped inside `buildDebugEntryKey`.
 * @returns {string} Stable key shared with the debug store.
 */
function debugWatchKey(kind: "query" | "paginatedQuery", name: string, args: unknown): string {
  return buildDebugEntryKey(kind, name, args);
}

/**
 * Writes a failed Convex watch into the client diagnostics bus.
 *
 * Only {@link recordWatchValue} calls this. The `status !== "error"` guard
 * means the `"pending"` / `"ready"` members on the parameter are dead — both
 * call sites that reach the sink already pass `"error"`. Outside development
 * {@link recordDiagnosticsFromClient} is itself a no-op, so production pays
 * only the early return.
 *
 * @param {ConvexDebugWatchRecord} record - Watch whose `name` becomes the
 *   diagnostics `action` and whose cloned `args` go in `context`.
 * @param {unknown} error - Thrown value or the `Error` `localQueryResult` returned.
 * @param {("error"|"pending"|"ready")} status - Ignored unless `"error"`.
 * @returns {void}
 */
function recordConvexQueryDiagnostics(
  record: ConvexDebugWatchRecord,
  error: unknown,
  status: "error" | "pending" | "ready",
) {
  if (status !== "error") {
    return;
  }
  const fields = errorToDiagnosticsFields(error);
  recordDiagnosticsFromClient({
    source: "convex",
    message: fields.message,
    stack: fields.stack,
    code: fields.code,
    action: record.name,
    context: {
      kind: record.kind,
      args: clone(record.args),
    },
  });
}

/**
 * Snapshots one watch into the debug store and, on failure, the diagnostics bus.
 *
 * Called from three places: the patched `onUpdate` wrappers (every live
 * result change), {@link scheduleDebugRecord}'s deferred first read, and
 * {@link flushDebugWatches} when recording is toggled on. Status is derived
 * from `localQueryResult()`: an `Error` instance is `"error"`, `undefined` is
 * `"pending"`, anything else is `"ready"`. The debug-store write is skipped
 * when recording is off; the diagnostics write still runs on error.
 *
 * @param {ConvexDebugWatchRecord} record - The watch to read. Not checked
 *   against {@link activeDebugWatches}, so a replaced or unmounted watch
 *   still records if its `onUpdate` fires.
 * @returns {void}
 */
function recordWatchValue(record: ConvexDebugWatchRecord) {
  const recordingEnabled = isConvexDebugRecordingEnabled();
  try {
    const result = record.watch.localQueryResult();
    const status = result instanceof Error ? "error" : result === undefined ? "pending" : "ready";
    if (recordingEnabled) {
      recordConvexDebugEntry({
        kind: record.kind,
        name: record.name,
        args: clone(record.args),
        result: clone(result),
        status,
      });
    }
    if (status === "error") {
      recordConvexQueryDiagnostics(record, result, status);
    }
  } catch (error) {
    if (recordingEnabled) {
      recordConvexDebugEntry({
        kind: record.kind,
        name: record.name,
        args: clone(record.args),
        result: clone(error),
        status: "error",
      });
    }
    recordConvexQueryDiagnostics(record, error, "error");
  }
}

/**
 * Reads every occupant of {@link activeDebugWatches} into the debug store.
 *
 * Scheduled only by {@link scheduleDebugFlush} (the recording-enabled
 * subscriber). Clears {@link debugFlushQueued} first so a subsequent toggle
 * can schedule again; then no-ops if recording was turned off before the
 * frame ran. Does not remove stale keys — unmounted queries stay in the map.
 *
 * @returns {void}
 */
function flushDebugWatches() {
  debugFlushQueued = false;
  if (!isConvexDebugRecordingEnabled()) {
    return;
  }
  for (const record of activeDebugWatches.values()) {
    recordWatchValue(record);
  }
}

/**
 * Coalesces a full-map snapshot onto the next animation frame.
 *
 * The only caller is the {@link subscribeConvexDebugRecordingEnabled}
 * listener installed by {@link ensureDebugEnabledSubscription} — i.e. this
 * runs when the debug panel's recording switch flips, not on every query
 * update. Falls back to `setTimeout(0)` when `requestAnimationFrame` is
 * missing (SSR / non-browser).
 *
 * @returns {void}
 */
function scheduleDebugFlush() {
  if (!isConvexDebugRecordingEnabled() || debugFlushQueued) {
    return;
  }
  debugFlushQueued = true;
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(flushDebugWatches);
    return;
  }
  setTimeout(flushDebugWatches, 0);
}

/**
 * Defers the first snapshot of a newly registered watch to the next frame.
 *
 * Only {@link registerDebugWatch} calls this, so a brand-new subscription is
 * recorded once without waiting for Convex to fire `onUpdate`. The deferred
 * callback drops the read when `record` is no longer the Map occupant for
 * `record.key` — a later watch of the same triple won the slot.
 *
 * @param {ConvexDebugWatchRecord} record - The record just inserted into
 *   {@link activeDebugWatches}.
 * @returns {void}
 */
function scheduleDebugRecord(record: ConvexDebugWatchRecord) {
  if (!isConvexDebugRecordingEnabled()) {
    return;
  }

  const flushRecord = () => {
    if (activeDebugWatches.get(record.key) !== record) {
      return;
    }
    recordWatchValue(record);
  };

  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(flushRecord);
    return;
  }
  setTimeout(flushRecord, 0);
}

/**
 * Subscribes once to the debug-store recording flag.
 *
 * {@link registerDebugWatch} calls this before inserting a record so that
 * turning recording on later flushes every watch already in the map (the
 * panel would otherwise stay empty until the next `onUpdate`). The
 * unsubscribe is stored on {@link debugEnabledSubscription} and never
 * invoked in this module.
 *
 * @returns {void}
 */
function ensureDebugEnabledSubscription() {
  if (debugEnabledSubscription) {
    return;
  }
  debugEnabledSubscription = subscribeConvexDebugRecordingEnabled(() => {
    scheduleDebugFlush();
  });
}

/**
 * Inserts a watch into {@link activeDebugWatches} and schedules its first read.
 *
 * Both debug wrappers inside {@link patchConvexClientForDebug} call this —
 * once for `watchQuery`, once for `watchPaginatedQuery`. A colliding
 * `(kind, name, args)` key overwrites the previous record; the old watch's
 * patched `onUpdate` is not unsubscribed and will still call
 * {@link recordWatchValue} with the displaced record.
 *
 * @param {("query"|"paginatedQuery")} kind - Which client method created `watch`.
 * @param {string} name - Convex function name, from `getFunctionName`.
 * @param {unknown} args - First argument passed to the watch method (`{}` when
 *   the caller omitted args).
 * @param {ConvexWatchLike} watch - The original Convex watch, captured by
 *   reference so later flushes can call `localQueryResult`.
 * @returns {ConvexDebugWatchRecord} The record now occupying `key`; the
 *   wrappers close over this so each `onUpdate` snapshots the same object.
 */
function registerDebugWatch(
  kind: "query" | "paginatedQuery",
  name: string,
  args: unknown,
  watch: ConvexWatchLike,
): ConvexDebugWatchRecord {
  ensureDebugEnabledSubscription();
  const key = debugWatchKey(kind, name, args);
  const record = { key, kind, name, args, watch };
  activeDebugWatches.set(key, record);
  scheduleDebugRecord(record);
  return record;
}

/**
 * Wraps `watchQuery` (and `watchPaginatedQuery` when present) so every
 * subscription feeds the Convex debug panel.
 *
 * Only {@link getConvexClient} calls this, and only when
 * `NODE_ENV === "development"`. The first call sets `__dwConvexDebugPatched`
 * on the singleton and replaces `watchQuery`; a second call returns the same
 * client unchanged. Production never enters this function, so the hooks
 * re-exported from this module talk to an unpatched client there.
 *
 * The replacement `onUpdate` still invokes the hook's callback first, then
 * {@link recordWatchValue}. `watchPaginatedQuery` is an optional own-method
 * — if this `convex/react` build does not have it, only `watchQuery` is
 * wrapped.
 *
 * @param {ConvexReactClient} client - The just-constructed singleton. Mutated
 *   in place; the return is the same object.
 * @returns {ConvexReactClient} `client`, now with debug wrappers installed.
 */
function patchConvexClientForDebug(client: ConvexReactClient): ConvexReactClient {
  const debuggableClient = client as ConvexReactClient & {
    watchPaginatedQuery?: (query: FunctionReference<"query">, args: unknown, options: unknown) => ConvexWatchLike;
    __dwConvexDebugPatched?: boolean;
  };
  if (debuggableClient.__dwConvexDebugPatched) {
    return client;
  }
  debuggableClient.__dwConvexDebugPatched = true;

  const originalWatchQuery = client.watchQuery.bind(client);

  function debugWatchQuery<Query extends FunctionReference<"query">>(
    query: Query,
    ...argsAndOptions: ArgsAndOptions<Query, WatchQueryOptions>
  ): Watch<FunctionReturnType<Query>> {
    const args = argsAndOptions[0] ?? {};
    const name = getFunctionName(query);
    const watch = originalWatchQuery(query, ...argsAndOptions);
    const record = registerDebugWatch("query", name, args, watch);
    return {
      ...watch,
      onUpdate: (callback: () => void) =>
        watch.onUpdate(() => {
          callback();
          recordWatchValue(record);
        }),
    };
  }

  client.watchQuery = debugWatchQuery;

  if (debuggableClient.watchPaginatedQuery) {
    const originalWatchPaginatedQuery = debuggableClient.watchPaginatedQuery.bind(debuggableClient);
    debuggableClient.watchPaginatedQuery = (query: FunctionReference<"query">, args: unknown, options: unknown) => {
      const name = getFunctionName(query);
      const watch = originalWatchPaginatedQuery(query, args, options);
      const record = registerDebugWatch("paginatedQuery", name, args, watch);
      return {
        ...watch,
        onUpdate: (callback: () => void) =>
          watch.onUpdate(() => {
            callback();
            recordWatchValue(record);
          }),
      };
    };
  }

  return client;
}

/**
 * Returns the process-wide {@link ConvexReactClient}, constructing it on first
 * call.
 *
 * The only caller in this repository is {@link ConvexClientProvider}, via
 * `useMemo(() => getConvexClient(), [])`. Root layout mounts that provider
 * once, so this is the socket every `useQuery` / `useMutation` under
 * `@/lib/convex/client` shares. Nothing else imports this function; server
 * code takes its HTTP client from `getConvexHttpClient` in `@/lib/convex/server`.
 *
 * Reads `NEXT_PUBLIC_CONVEX_URL` at construction time. In development the
 * returned instance is {@link patchConvexClientForDebug}'d so the assistant
 * debug panel can see live watches; in production the constructor result is
 * stored as-is.
 *
 * @returns {ConvexReactClient} The module singleton. Subsequent calls return
 *   the same object, including across provider remounts.
 * @throws {Error} When `NEXT_PUBLIC_CONVEX_URL` is unset. The message tells
 *   the operator to run `npx convex dev`. The provider does not catch this,
 *   so a missing URL crashes first client render.
 *
 * @example
 * // Only the provider calls this; a second call returns the same instance.
 * const client = getConvexClient();
 * const again = getConvexClient();
 * // again === client
 *
 * @see {@link ConvexClientProvider} — the layout wrapper that owns the only call.
 */
export function getConvexClient(): ConvexReactClient {
  if (convexClient) {
    return convexClient;
  }

  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    throw new Error("NEXT_PUBLIC_CONVEX_URL environment variable is not set. " + "Please run `npx convex dev` to configure your Convex project.");
  }

  // RootLayout mints a fresh token for every request, so the client keeps that token until it nears
  // expiry. Without this, Convex fetches a second token as soon as the first is confirmed, and that
  // second Authenticate re-executes every authenticated query on every page load.
  const client = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL, { initialAuthTokenReuse: true });
  convexClient = process.env.NODE_ENV === "development" ? patchConvexClientForDebug(client) : client;
  return convexClient;
}

// ============================================================================
// CONVEX PROVIDER WITH BETTER AUTH
// ============================================================================
/**
 * Props for {@link ConvexClientProvider}.
 *
 * `initialToken` is optional on the type, but {@link ConvexClientProvider}'s
 * only call site — `RootLayout` — always passes `getToken() ?? null` so
 * `ConvexBetterAuthProvider` can hydrate the Convex JWT on first paint
 * instead of waiting for the browser session fetch. `children` render inside
 * the Better Auth Convex provider, after {@link DiagnosticsBootstrap}.
 *
 * @typedef {{children: ReactNode, initialToken?: (string|null)}} ConvexClientProviderProps
 */
/**
 * Mounts the Convex React client and Better Auth session around `children`.
 *
 * `RootLayout` is the only live mount: it sits inside `AuthProvider` and
 * wraps `TRPCProvider` / theme / the rest of the app.
 * `useMemo(() => getConvexClient(), [])` is the sole {@link getConvexClient}
 * call site.
 *
 * Always renders {@link DiagnosticsBootstrap} as a sibling of `children`.
 * That component's effect no-ops outside development, but the element is
 * in the tree in every environment. The `authClient as unknown as AuthClient`
 * cast is required: `@convex-dev/better-auth` 0.12.5 types `AuthClient` as
 * an intersection that collapses `useSession().data` to `never`, so no real
 * client satisfies the prop until upstream fixes it.
 *
 * `data-component` is `"ConvexClientProvider"`.
 *
 * @param {ConvexClientProviderProps} props - Children plus the optional
 *   server token.
 * @param {ReactNode} props.children - App tree that may call Convex hooks.
 * @param {(string|null)} [props.initialToken] - Convex JWT from `getToken()`,
 *   forwarded to `ConvexBetterAuthProvider`.
 * @returns {JSX.Element} `ConvexBetterAuthProvider` wrapping diagnostics
 *   bootstrap and `children`.
 */
/** How long a read stays subscribed after it unmounts (every read goes through this cache, lib/convex/hooks/useAuthedQuery):
 *  long enough for a prewarmed read to reach the page that asked for it, and for a quick return to find its rows. */
const LIST_CACHE_EXPIRATION_MS = 5 * 60_000;

export const ConvexClientProvider = memo(function ConvexClientProvider(
  { children, initialToken }: {
    children: ReactNode;
    initialToken?: string | null;
  }
) {
  const client = useMemo(() => getConvexClient(), []);

  return (
    // MERGE-ADAPT (2026-08-02): carried over from main-next, which hit this on
    // the @convex-dev/better-auth 0.12.5 bump. 0.12.5 types AuthClient as
    // `createAuthClient<BetterAuthClientPlugin & { plugins }>`; that
    // intersection collapses `useSession().data` to `never`, so no real client
    // can satisfy it. 0.12.5 is the latest release, so the cast stands until
    // upstream fixes the type.
    // eslint-disable-next-line dw/no-double-cast -- @convex-dev/better-auth 0.12.5 AuthClient intersection collapses useSession().data to never; no real client satisfies it until upstream fixes the type.
    <ConvexBetterAuthProvider client={client} authClient={authClient as unknown as AuthClient} initialToken={initialToken} data-component="ConvexClientProvider" data-location="src/lib/convex/client.tsx">
      <ConvexQueryCacheProvider expiration={LIST_CACHE_EXPIRATION_MS}>
        <DiagnosticsBootstrap />
        {children}
      </ConvexQueryCacheProvider>
    </ConvexBetterAuthProvider>
  );
});
